import React from 'react';
import { Box, Typography, Paper, Grid, Chip } from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import StorageIcon from '@mui/icons-material/Storage';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import ShieldIcon from '@mui/icons-material/Shield';
import AssessmentIcon from '@mui/icons-material/Assessment';

const HomePage = () => {
  const moduleStyle = {
    p: 2.5,
    height: '100%',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    transition: 'all 0.2s',
    cursor: 'default',
    '&:hover': { borderColor: '#1abc9c', boxShadow: '0 2px 8px rgba(26,188,156,0.15)' }
  };

  return (
    <Box sx={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>
      {/* Hero */}
      <Paper sx={{ p: 4, mb: 4, background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 100%)', borderRadius: '12px', color: 'white' }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          📊 P&C Insurance Pricing Platform
        </Typography>
        <Typography variant="body1" sx={{ mb: 2, opacity: 0.9, maxWidth: '800px' }}>
          A comprehensive toolkit for <strong>Property & Casualty insurance pricing</strong>. 
          Build, calibrate, and validate <strong>frequency</strong> and <strong>severity</strong> 
          models to calculate pure premium and technical pricing with advanced statistical techniques.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip label="Frequency Modelling" sx={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 500 }} />
          <Chip label="Severity Modelling" sx={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 500 }} />
          <Chip label="Pure Premium" sx={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 500 }} />
          <Chip label="Extreme Value Theory" sx={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 500 }} />
          <Chip label="GLM Regression" sx={{ background: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 500 }} />
        </Box>
      </Paper>

      {/* What is Frequency & Severity */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
        Understanding Frequency & Severity Modelling
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={moduleStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#2980b9', mb: 1 }}>
              📈 Frequency Modelling
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#555', lineHeight: 1.7 }}>
              <strong>Frequency</strong> models predict the expected <strong>number of claims</strong> 
              for a given policy or risk exposure. Common approaches include:
            </Typography>
            <Paper sx={{ p: 1.5, background: '#eaf2f8', mb: 1.5, textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#2471a3', borderRadius: '6px' }}>
              E[N] = Exposure × exp(β₀ + β₁X₁ + ...)
            </Paper>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
              <strong>Methods:</strong> Poisson Regression, Negative Binomial, Zero-Inflated models.
              Predict claim counts based on risk characteristics like coverage type, location, and policy limits.
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip label="Poisson GLM" size="small" sx={{ mr: 0.5, background: '#eaf2f8', color: '#2471a3' }} />
              <Chip label="NegBin" size="small" sx={{ mr: 0.5, background: '#eaf2f8', color: '#2471a3' }} />
              <Chip label="Exposure Offset" size="small" sx={{ background: '#eaf2f8', color: '#2471a3' }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={moduleStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#e74c3c', mb: 1 }}>
              💰 Severity Modelling
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#555', lineHeight: 1.7 }}>
              <strong>Severity</strong> models predict the expected <strong>cost per claim</strong>.
              These are typically continuous positive values modeled with:
            </Typography>
            <Paper sx={{ p: 1.5, background: '#fdf2f2', mb: 1.5, textAlign: 'center', fontWeight: 600, fontSize: '14px', color: '#c0392b', borderRadius: '6px' }}>
              E[Cost|Claim] = exp(α₀ + α₁Z₁ + ...)
            </Paper>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
              <strong>Methods:</strong> Gamma Regression, Weibull, Log-Normal models.
              Estimate average claim cost based on claim type, cause, and policy characteristics.
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip label="Gamma GLM" size="small" sx={{ mr: 0.5, background: '#fde8e8', color: '#c0392b' }} />
              <Chip label="Weibull" size="small" sx={{ mr: 0.5, background: '#fde8e8', color: '#c0392b' }} />
              <Chip label="Log-Normal" size="small" sx={{ background: '#fde8e8', color: '#c0392b' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Pure Premium */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
        Pure Premium Calculation
      </Typography>
      <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #f8f9fa, #e8f8f5)', borderRadius: '10px', border: '1px solid #1abc9c' }}>
        <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7, mb: 2 }}>
          The <strong>Pure Premium</strong> is the expected loss cost per exposure, calculated by combining 
          frequency and severity models. This forms the foundation of technical pricing before adding 
          expenses, profit margin, and risk loading.
        </Typography>
        <Paper sx={{ p: 2, background: 'white', textAlign: 'center', fontWeight: 700, fontSize: '16px', color: '#1abc9c', borderRadius: '8px', mb: 2 }}>
          Pure Premium = E[Frequency] × E[Severity]
        </Paper>
        <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
          After calculating the pure premium, actuaries apply <strong>loading factors</strong> for:
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
          <Chip label="Expenses" size="small" />
          <Chip label="Profit Margin" size="small" />
          <Chip label="Risk Margin" size="small" />
          <Chip label="Reinsurance Cost" size="small" />
          <Chip label="Commission" size="small" />
        </Box>
      </Paper>

      {/* Large Claims & Extreme Value Theory */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
        🔺 Large & Extra-Large Claims: Extreme Value Theory
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={moduleStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#e74c3c', mb: 1 }}>
              🔴 Large Claims (Attritional)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#555', lineHeight: 1.7 }}>
              Large claims are infrequent but significant losses that fall within the 
              <strong> body of the distribution</strong> but above typical attritional levels. 
              These are modeled using standard GLM techniques with appropriate tail behavior.
            </Typography>
            <Paper sx={{ p: 1.5, background: '#fdf2f2', mb: 1.5, textAlign: 'center', fontWeight: 600, fontSize: '13px', color: '#c0392b', borderRadius: '6px' }}>
              Attritional Threshold &lt; Large Claims &lt; Extreme Threshold
            </Paper>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
              <strong>Characteristics:</strong> 5-15% of claims, 20-40% of total losses.
              Modeled separately from attritional claims for better segmentation.
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip label="Severity GLM" size="small" sx={{ mr: 0.5, background: '#fde8e8', color: '#c0392b' }} />
              <Chip label="Threshold Selection" size="small" sx={{ mr: 0.5, background: '#fde8e8', color: '#c0392b' }} />
              <Chip label="Segmentation" size="small" sx={{ background: '#fde8e8', color: '#c0392b' }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={moduleStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ color: '#8e44ad', mb: 1 }}>
              🟣 Extra-Large Claims (Extreme)
            </Typography>
            <Typography variant="body2" sx={{ mb: 1.5, color: '#555', lineHeight: 1.7 }}>
              Extra-large claims are <strong>extreme outliers</strong> in the tail of the distribution.
              These require <strong>Extreme Value Theory (EVT)</strong> with Generalized Pareto Distribution (GPD) 
              to model exceedances above a high threshold.
            </Typography>
            <Paper sx={{ p: 1.5, background: '#f4ecf7', mb: 1.5, textAlign: 'center', fontWeight: 600, fontSize: '13px', color: '#7d3c98', borderRadius: '6px' }}>
              Claims &gt; Threshold → GPD(ξ, σ) with Peaks Over Threshold
            </Paper>
            <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.7 }}>
              <strong>Characteristics:</strong> 0.5-2% of claims, 10-30% of total losses.
              Heavy-tailed behavior captured by GPD shape parameter (ξ).
            </Typography>
            <Box sx={{ mt: 1.5 }}>
              <Chip label="GPD Fit" size="small" sx={{ mr: 0.5, background: '#ebdef0', color: '#7d3c98' }} />
              <Chip label="VaR / TVaR" size="small" sx={{ mr: 0.5, background: '#ebdef0', color: '#7d3c98' }} />
              <Chip label="Return Level" size="small" sx={{ background: '#ebdef0', color: '#7d3c98' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* EVT Methods Summary */}
      <Paper sx={{ p: 3, mb: 4, background: 'linear-gradient(135deg, #fdf2f2, #f4ecf7)', borderRadius: '10px', border: '1px solid #e74c3c' }}>
        <Typography variant="h6" fontWeight={600} sx={{ color: '#2c3e50', mb: 2 }}>
          🔺 Extreme Value Theory — Key Concepts
        </Typography>
        <Grid container spacing={2}>
          {[
            { title: 'Mean Excess Plot', desc: 'Graphical tool to identify the optimal threshold where the mean excess function becomes linear, indicating the start of the tail region.' },
            { title: 'Peaks Over Threshold (POT)', desc: 'Models all observations exceeding a high threshold using the Generalized Pareto Distribution (GPD).' },
            { title: 'GPD Parameters', desc: 'Shape (ξ) controls tail heaviness: ξ>0 heavy-tailed, ξ=0 exponential, ξ<0 bounded. Scale (σ) measures dispersion.' },
            { title: 'Value at Risk (VaR)', desc: 'The loss level that will not be exceeded with a given confidence level (e.g., 99.5% VaR).' },
            { title: 'Tail Value at Risk (TVaR)', desc: 'Expected loss given that the loss exceeds VaR. Captures the severity of extreme tail events.' },
            { title: 'Return Levels', desc: 'The loss level expected to be exceeded once every T years (e.g., 1-in-200 year return level).' }
          ].map((item, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Paper sx={{ p: 2, borderRadius: '8px', border: '1px solid #e0e0e0', height: '100%' }}>
                <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#e74c3c', mb: 0.5 }}>{item.title}</Typography>
                <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>{item.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Key Features */}
      <Typography variant="h5" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
        Key Features
      </Typography>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {[
          { icon: '📊', title: 'Frequency Modelling', desc: 'Poisson, Negative Binomial, Binomial GLMs with link functions, offsets, and interactions.' },
          { icon: '💰', title: 'Severity Modelling', desc: 'Gamma, Weibull, Normal GLMs for claim cost estimation with diagnostic plots.' },
          { icon: '💎', title: 'Pure Premium', desc: 'Combine frequency × severity models with loading factors for technical pricing.' },
          { icon: '🔺', title: 'Large & Extra-Large Claims', desc: 'EVT with GPD for tail modeling, threshold selection via Mean Excess Plot, VaR/TVaR calculation.' },
          { icon: '📈', title: 'Data Analysis', desc: 'Missing values, discretization, outlier detection for robust data preparation.' },
          { icon: '💾', title: 'Save & Export', desc: 'Save analyses, models, and export results to Excel with charts.' }
        ].map((f, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Paper sx={{ p: 2.5, borderRadius: '8px', border: '1px solid #e0e0e0', height: '100%', '&:hover': { borderColor: '#1abc9c' } }}>
              <Typography variant="h6" sx={{ mb: 0.5 }}>{f.icon} {f.title}</Typography>
              <Typography variant="body2" sx={{ color: '#7f8c8d', lineHeight: 1.6 }}>{f.desc}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default HomePage;
